// Mock-story registry (visual-first demo).
//
// The rich panels (list / table / board / roadmap / dependency graph) render
// deterministic MOCK stories whenever the real API feed is empty. Those mock
// rows carry ids like `<missionId>-s<n>` that do NOT exist server-side. The
// StoryDrawer is self-fetching — given only officeId + storyId it hits
// `/api/offices/:id/stories/:storyId`, which 404s for a mock id, so the drawer
// shows "Story not found".
//
// Fix: panels register the mock rows they actually render here; the drawer falls
// back to this registry when an id isn't a real API story. Mock ids never
// collide with real story ids, so a registry hit unambiguously means "this is a
// mock story — render it locally, don't fetch."
//
// This is a module-level cache on purpose: the producing hook (useTaskList) and
// the consuming hook (useStoryDrawer) live in sibling component trees with no
// shared React context, and the data is deterministic, so a plain map is the
// simplest reliable channel.
//
// On a DIRECT open (deep link / annotation) the drawer renders BEFORE the panel
// has a chance to register — so a registry miss falls back to deriving the story
// from its id. Without this the drawer fires ~6 doomed 404 requests and flashes
// "Story not found" before self-healing — which reads as "clicking is slow".

import { deriveMockStory } from './board-stories.js';
import { mockStories } from '@/components/project-view/_mock/sampleData.js';

const REGISTRY = new Map();

/** Record the mock rows a panel is rendering so the drawer can resolve them. */
export function registerMockStories(stories) {
  for (const s of stories ?? []) {
    if (s?.id) REGISTRY.set(s.id, s);
  }
}

// The shared generic sample (used by missions without their own board spec) has
// ids that aren't derivable from a mission id, so seed them eagerly.
registerMockStories(mockStories);

/**
 * The mock story for an id, or null if it's not a known mock story.
 * PURE — no registry writes (must be safe to call during render). On a miss it
 * derives synchronously and deterministically (deriveMockStory is memoized, so
 * repeated calls return the SAME object reference — important for stable React
 * deps). Reverse ("blocks") deps are resolved by the caller via
 * boardStoriesByMissionId, not by mutating the registry here.
 */
export function mockStoryById(id) {
  if (!id) return null;
  return REGISTRY.get(id) ?? deriveMockStory(id);
}

/** Every registered mock story — used to resolve reverse ("blocks") deps. */
export function allMockStories() {
  return [...REGISTRY.values()];
}
